import {
  Box,
  Group,
  Pagination,
  Popover,
  Stack,
  Table,
  TableTbody,
  TableTd,
  TableTr,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconSearch, IconUserPlus } from "@tabler/icons-react"
import { Badge, Button, ErrorBox, Loader } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router-dom"

import { usePagination } from "~/components/hooks/usePagination"

import { ChangeBusinessType } from "~/pages/Admin/Organizations/ChangeBusinessType"
import { InviteUsersDrawer } from "~/pages/Admin/Organizations/InviteUsersDrawer"
import { ToggleOrganizationUsersStatusButton } from "~/pages/Admin/Organizations/ToggleOrganizationUsersStatusButton/ToggleOrganizationUsersStatusButton"
import { GenerateMagicLinkButton } from "~/pages/Admin/components/GenerateMagicLink"
import { MuteButton } from "~/pages/Brand/Suppliers/List/components/MuteButton"
import { useAuth } from "~/store/auth-context"

import { UserOptions } from "./UserOptions/UserOptions"
import {
  useCreateCountryContact,
  useCreateCountryContactForOrganization,
  useLoginAsUserMutation,
} from "./hooks"
import { useFindAdminOrganizationSuppliers } from "~/gen/api/hooks"
import { FindOrganizationByIdResponseDto } from "~/gen/api"
import { useSearchSupplier } from "~/pages/Admin/Organizations/hooks/useSearchSupplier"

type SupplierSearchInputProps = {
  onDebouncedSupplierSearchChange: (value: string) => void
}

function SupplierSearchInput({
  onDebouncedSupplierSearchChange,
}: SupplierSearchInputProps) {
  const { t } = useTranslation("admin")
  const { supplierSearch, setSupplierSearch, debouncedSupplierSearch } =
    useSearchSupplier()

  useEffect(() => {
    onDebouncedSupplierSearchChange(debouncedSupplierSearch)
  }, [debouncedSupplierSearch, onDebouncedSupplierSearchChange])

  return (
    <TextInput
      value={supplierSearch}
      onChange={(event) => {
        setSupplierSearch(event.currentTarget.value)
      }}
      size="xs"
      maw={320}
      placeholder={t("suppliers.search.placeholder")}
      leftSection={<IconSearch size={16} />}
    />
  )
}

export const OrganizationDetails = () => {
  const { t } = useTranslation("admin")
  const [addUsersOpen, addUsersActions] = useDisclosure(false)
  const [debouncedSupplierSearch, setDebouncedSupplierSearch] =
    useState<string>("")
  const {
    cursorBasedPagination,
    pageBasedPagination,
    calculateTotalPages,
    setPageBasedPagination,
  } = usePagination({
    pagination: {
      skip: 0,
      take: 25,
    },
  })

  const organization = useOutletContext<FindOrganizationByIdResponseDto>()
  const {
    data: suppliersQuery,
    isLoading: isLoadingSuppliers,
    error: errorForSuppliers,
    isError: isErrorSuppliers,
  } = useFindAdminOrganizationSuppliers(organization.id, {
    ...cursorBasedPagination,
    search:
      debouncedSupplierSearch.trim().length > 0
        ? debouncedSupplierSearch.trim()
        : undefined,
  })

  const suppliers = suppliersQuery?.data?.suppliers ?? []
  const totalSuppliers = suppliersQuery?.data?.total ?? 0
  const totalSuppliersPages = calculateTotalPages(
    pageBasedPagination.pageSize,
    totalSuppliers
  )

  useEffect(() => {
    setPageBasedPagination((previousPagination) => {
      if (previousPagination.pageIndex === 0) {
        return previousPagination
      }

      return {
        ...previousPagination,
        pageIndex: 0,
      }
    })
  }, [debouncedSupplierSearch, setPageBasedPagination])

  return (
    <Stack gap="sm">
      <Stack>
        <Title>{organization.name}</Title>
        <Text size="xs" c="dimmed">
          Created on{" "}
          {new Intl.DateTimeFormat().format(new Date(organization.createdAt))}
        </Text>
      </Stack>
      <Stack>
        <Title order={2}>Other settings</Title>
        <ChangeBusinessType organization={organization} />
      </Stack>
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={2}>Users</Title>
          <Group gap={4}>
            <ToggleOrganizationUsersStatusButton
              organizationId={organization.id}
            />
            <Button
              onClick={addUsersActions.open}
              icon={<IconUserPlus size={16} />}
            >
              Create Users
            </Button>
          </Group>
        </Group>
        {organization.users.length === 0 ? (
          <Text>No users</Text>
        ) : (
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Tbody>
              {organization.users.map((user) => (
                <TableTr key={user.id}>
                  <TableTd>
                    <Group>
                      <Stack gap={4}>
                        <Text style={{ fontWeight: "bold" }}>{user.name}</Text>
                        <Group wrap="nowrap">
                          <Text>{user.email}</Text>
                          {!user.active && (
                            <Badge variant="error" text="Inactive" ml={4} />
                          )}
                        </Group>
                      </Stack>
                    </Group>
                  </TableTd>
                  <TableTd style={{ textAlign: "right" }}>
                    <Group gap={4} justify="flex-end">
                      {user.active && (
                        <LoginAs
                          user={user}
                          withoutAdmin={true}
                          onLogin={() => window.location.replace("/")}
                        />
                      )}
                      <UserOptions organization={organization} user={user} />
                    </Group>
                  </TableTd>
                </TableTr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={3}>Suppliers</Title>
          <CreateCountryContactButton
            organizationId={organization.id}
            suppliersCount={totalSuppliers}
          />
        </Group>
        <SupplierSearchInput
          onDebouncedSupplierSearchChange={setDebouncedSupplierSearch}
        />
        {isErrorSuppliers && <ErrorBox error={errorForSuppliers} />}
        {isLoadingSuppliers ? (
          <Loader fullPage />
        ) : suppliers.length === 0 ? (
          <Text>
            {debouncedSupplierSearch.trim().length > 0
              ? t("suppliers.list.noSuppliersMatchSearch")
              : t("suppliers.list.noSuppliers")}
          </Text>
        ) : (
          <>
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Tbody>
                {suppliers.map((supplier) => (
                  <TableTr
                    data-testid={supplier.user.email}
                    key={supplier.user.id}
                  >
                    <TableTd width="200">
                      <Text style={{ fontWeight: "bold" }}>
                        {supplier.user.name}
                      </Text>
                      <Text>{supplier.user.email}</Text>
                    </TableTd>
                    <TableTd>
                      <Stack
                        align="flex-start"
                        gap="var(--tfg-molecule-padding-y)"
                      >
                        <LoginAs
                          user={supplier.user}
                          fullWidth={true}
                          withoutAdmin={true}
                          onLogin={() =>
                            window.open("/supplier/requests", "_blank")
                          }
                        />
                        <CreateCountryContactButton
                          userEmails={[supplier.user.email]}
                        />
                      </Stack>
                    </TableTd>

                    <TableTd>
                      <Stack
                        mah={200}
                        style={{ overflowY: "scroll" }}
                        maw={200}
                      >
                        {supplier.contactForProductionSite.map((c) => (
                          <Group key={c.id}>
                            <Text py="sm">{c.name}</Text>
                          </Group>
                        ))}
                      </Stack>
                    </TableTd>
                    <TableTd>
                      <Table>
                        <TableTbody>
                          <TableTr>
                            <TableTd>
                              <Text fw={600}>Information Requests</Text>
                            </TableTd>
                            <TableTd>
                              <Text fw={600}>Certificate Requests</Text>
                            </TableTd>
                            <TableTd>
                              <Text fw={600}>Is direct for</Text>
                            </TableTd>
                          </TableTr>
                          <TableTr>
                            <TableTd>
                              <Text>
                                {supplier.informationRequestOpenCount} /{" "}
                                {supplier.informationRequestCloseCount}
                              </Text>
                            </TableTd>
                            <TableTd>
                              <Text>
                                {supplier.openCertificateRequests} /{" "}
                                {supplier.closedCertificateRequests}
                              </Text>
                            </TableTd>
                            <TableTd>
                              <Text>{supplier.isDirectForXProduct}</Text>
                            </TableTd>
                          </TableTr>
                          <TableTr>
                            <TableTd>
                              <Text>Open / Closed</Text>
                            </TableTd>
                            <TableTd>
                              <Text>Open / Closed</Text>
                            </TableTd>
                            <TableTd>
                              <Text>Products</Text>
                            </TableTd>
                          </TableTr>
                        </TableTbody>
                      </Table>
                    </TableTd>
                    <TableTd>
                      <Table>
                        <TableTbody>
                          <TableTr>
                            <TableTd>
                              <Stack justify="center" gap={8}>
                                <MuteButton
                                  userId={supplier.user.id}
                                  hasMutedReminders={
                                    supplier.user.hasMutedReminders
                                  }
                                />
                                <GenerateMagicLinkButton
                                  userId={supplier.user.id}
                                />
                              </Stack>
                            </TableTd>
                          </TableTr>
                        </TableTbody>
                      </Table>
                    </TableTd>
                  </TableTr>
                ))}
              </Table.Tbody>
            </Table>
            {totalSuppliersPages > 1 && (
              <Group justify="flex-end">
                <Pagination
                  total={totalSuppliersPages}
                  value={pageBasedPagination.pageIndex + 1}
                  onChange={(page) => {
                    setPageBasedPagination((prev) => ({
                      ...prev,
                      pageIndex: page - 1,
                    }))
                  }}
                />
              </Group>
            )}
          </>
        )}
      </Stack>
      <InviteUsersDrawer
        opened={addUsersOpen}
        organization={organization}
        onClose={addUsersActions.close}
      />
    </Stack>
  )
}

type LoginAsProps = {
  user: {
    id: string
    email: string
    name: string
  }
  showEmail?: boolean
  withoutAdmin: boolean
  onLogin: () => void
  fullWidth?: boolean
}

export function LoginAs(props: LoginAsProps) {
  const { mutateAsync: loginAsUser } = useLoginAsUserMutation()
  const { checkAuth } = useAuth()
  const { user, withoutAdmin, onLogin, showEmail, fullWidth } = props

  return (
    <>
      <Button
        variant="primary"
        fullWidth={fullWidth}
        data-user-email={user.email}
        data-testid="login-as-user"
        toolTip={`you will log as ${user.email} but still see admin buttons and page, ALL the action will be registered as if you were ${user.email} (change operation, submiting...)`}
        toolTipProps={{ multiline: true, w: 300 }}
        onClick={() =>
          loginAsUser({
            userId: user.id,
            forwardRole: true,
          }).then(() => {
            checkAuth()
            onLogin()
          })
        }
      >
        Log in as{showEmail && ` ${user.email}`}
      </Button>
      {withoutAdmin && (
        <>
          {" "}
          <Button
            fullWidth={fullWidth}
            variant="secondary"
            color="secondary"
            toolTip={`you will log exactly is as if you were ${user.email}, and will need to logout first to go back to admin page`}
            toolTipProps={{ multiline: true, w: 300 }}
            onClick={() =>
              loginAsUser({
                userId: user.id,
                forwardRole: false,
              }).then(() => {
                checkAuth()
                onLogin()
              })
            }
          >
            without admin privilege
          </Button>
        </>
      )}
    </>
  )
}

type CreateCountryContactButtonProps =
  | {
      userEmails: string[]
      organizationId?: undefined
      suppliersCount?: undefined
    }
  | {
      userEmails?: undefined
      organizationId: string
      suppliersCount: number
    }

function CreateCountryContactButton(props: CreateCountryContactButtonProps) {
  const userEmails = props.userEmails ?? []
  const {
    mutate: createCountryContactForEmails,
    isPending: isLoadingForEmails,
    error: errorForEmails,
  } = useCreateCountryContact(userEmails)
  const {
    mutate: createCountryContactForOrganization,
    isPending: isLoadingForOrganization,
    error: errorForOrganization,
  } = useCreateCountryContactForOrganization(props.organizationId)

  const isOrganizationAction = props.organizationId !== undefined
  const createCountryContact = isOrganizationAction
    ? createCountryContactForOrganization
    : createCountryContactForEmails
  const isLoading = isOrganizationAction
    ? isLoadingForOrganization
    : isLoadingForEmails
  const error = isOrganizationAction ? errorForOrganization : errorForEmails

  const [opened, { open, close }] = useDisclosure(false)

  return (
    <>
      <Popover
        position="bottom"
        withArrow
        shadow="md"
        opened={opened}
        closeOnEscape={true}
        closeOnClickOutside={true}
        onClose={close}
      >
        <Popover.Target>
          <Box>
            <Button
              w="100%"
              loading={isLoading}
              variant="tertiary"
              onClick={open}
              justify="center"
            >
              {isOrganizationAction ? (
                <>
                  Create country contact for all suppliers (
                  {props.suppliersCount})
                </>
              ) : userEmails.length > 1 ? (
                <>Create country contact for {userEmails.length} suppliers</>
              ) : (
                <>Create country contact</>
              )}
            </Button>
          </Box>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack>
            <Text size="xs">Are you sure ? it can be hard to rollback</Text>
            <Button
              variant="primary"
              color="red"
              loading={isLoading}
              onClick={() => {
                close()
                createCountryContact()
              }}
            >
              Yes, create
            </Button>
          </Stack>
        </Popover.Dropdown>
      </Popover>
      {error && <Text c="red">{error.message}</Text>}
    </>
  )
}
